import type { ClientTier } from "@klarity/schema";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api/endpoints";
import type { ItemFilter } from "./api/models";

export const qk = {
  me: ["me"] as const,
  spaces: ["spaces"] as const,
  tree: (spaceId: string) => ["tree", spaceId] as const,
  internalDocs: ["internalDocs"] as const,
  doc: (ref: string) => ["doc", ref] as const,
  theme: (themeId: string | null) => ["theme", themeId ?? "default"] as const,
  themes: ["themes"] as const,
  themeVersions: (themeId: string) => ["themeVersions", themeId] as const,
  templates: ["templates"] as const,
  revisions: (docId: string) => ["revisions", docId] as const,
  revision: (docId: string, v: number) => ["revision", docId, v] as const,
  diff: (docId: string, v: number) => ["diff", docId, v] as const,
  search: (q: string) => ["search", q] as const,
  members: (spaceId: string) => ["members", spaceId] as const,
  users: ["users"] as const,
  tokens: ["tokens"] as const,
  audit: (action: string) => ["audit", action] as const,
  archivedSpaces: ["archivedSpaces"] as const,
  clients: ["clients"] as const,
  gorelo: ["gorelo"] as const,
  sites: (clientId: string) => ["sites", clientId] as const,
  /** Prefix ["items", clientId] matches every filter of that client. */
  items: (clientId: string, filter: ItemFilter = {}) => ["items", clientId, filter] as const,
  item: (itemId: string) => ["item", itemId] as const,
  diagram: (siteId: string) => ["diagram", siteId] as const,
  links: (docId: string) => ["links", docId] as const,
  inventory: (q: string) => ["inventory", q] as const,
};

export const useSpaces = () => useQuery({ queryKey: qk.spaces, queryFn: api.spaces.list });
export const useTree = (spaceId: string | undefined) =>
  useQuery({ queryKey: qk.tree(spaceId ?? ""), queryFn: () => api.tree(spaceId ?? ""), enabled: Boolean(spaceId) });
// Always stale: an archive, a restore, a new document or a changed code anywhere changes the Handbook's counts,
// so the page reads them again each time it opens.
export const useInternalDocuments = () =>
  useQuery({ queryKey: qk.internalDocs, queryFn: api.internalDocuments, staleTime: 0 });
export const useDocument = (ref: string, { refetchOnWindowFocus = true } = {}) =>
  useQuery({ queryKey: qk.doc(ref), queryFn: () => api.documents.get(ref), retry: false, refetchOnWindowFocus });
export const usePublishedTheme = (themeId: string | null, enabled = true) =>
  useQuery({
    queryKey: qk.theme(themeId),
    queryFn: () => api.themes.published(themeId),
    enabled,
    staleTime: 5 * 60_000,
  });
export const useTemplates = () => useQuery({ queryKey: qk.templates, queryFn: api.templates });
export const useThemes = () => useQuery({ queryKey: qk.themes, queryFn: api.themes.list });
export const useSearch = (q: string) =>
  useQuery({ queryKey: qk.search(q), queryFn: () => api.search(q, { limit: 20 }), enabled: q.trim().length >= 2 });
// One request feeds both: the list and the sync info are selected from the same cached answer.
export const useClients = () =>
  useQuery({ queryKey: qk.clients, queryFn: api.clients.overview, select: (overview) => overview.items });
export const useClientSync = () =>
  useQuery({ queryKey: qk.clients, queryFn: api.clients.overview, select: (overview) => overview.sync });
/**
 * Sets a client's tier. The client list is fetched again after a failure too: a client that is gone leaves the
 * page. The mutation stays pending until that list is in, so the row is in its new section when it ends.
 */
export function useSetClientTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tier }: { id: string; tier: ClientTier | null }) => api.clients.setTier(id, tier),
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.clients }),
  });
}
export const useSites = (clientId: string | undefined) =>
  useQuery({
    queryKey: qk.sites(clientId ?? ""),
    queryFn: () => api.sites.list(clientId ?? ""),
    enabled: Boolean(clientId),
  });
export const useItems = (clientId: string | undefined, filter: ItemFilter = {}) =>
  useQuery({
    queryKey: qk.items(clientId ?? "", filter),
    queryFn: () => api.items.list(clientId ?? "", filter),
    enabled: Boolean(clientId),
  });
export const useItem = (itemId: string) =>
  useQuery({ queryKey: qk.item(itemId), queryFn: () => api.items.get(itemId), retry: false });
export const useDiagram = (siteId: string) =>
  useQuery({ queryKey: qk.diagram(siteId), queryFn: () => api.diagram.get(siteId), retry: false });
export const useDocLinks = (docId: string, enabled = true) =>
  useQuery({ queryKey: qk.links(docId), queryFn: () => api.links.get(docId), enabled });
export const useInventorySearch = (q: string) =>
  useQuery({
    queryKey: qk.inventory(q.trim()),
    queryFn: () => api.searchInventory(q),
    enabled: q.trim().length >= 2 && q.trim().length <= 100,
  });

/** After a site, item or document-link change: the client's lists, its counts and open item details refetch. */
export function refreshInventory(queryClient: QueryClient, clientId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["items", clientId] }),
    queryClient.invalidateQueries({ queryKey: qk.sites(clientId) }),
    queryClient.invalidateQueries({ queryKey: ["item"] }),
    queryClient.invalidateQueries({ queryKey: ["diagram"] }),
    queryClient.invalidateQueries({ queryKey: qk.clients }),
  ]);
}

/** After a sync: clients, spaces, sites, items and the Gorelo status may all have changed. */
export function refreshAfterSync(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: qk.clients }),
    queryClient.invalidateQueries({ queryKey: qk.spaces }),
    queryClient.invalidateQueries({ queryKey: qk.archivedSpaces }),
    queryClient.invalidateQueries({ queryKey: ["sites"] }),
    queryClient.invalidateQueries({ queryKey: ["items"] }),
    queryClient.invalidateQueries({ queryKey: ["item"] }),
    queryClient.invalidateQueries({ queryKey: ["diagram"] }),
    queryClient.invalidateQueries({ queryKey: ["inventory"] }),
    queryClient.invalidateQueries({ queryKey: qk.gorelo }),
  ]);
}
